import React, {useState} from 'react'
import {Box} from '@mui/material'
import AppDialog from 'src/components-new/feedback/AppDialog'
import Button from 'src/components-new/primitives/Button'
import Card from 'src/components-new/layout/Card'
import SelectNew from 'src/components-new/inputs/SelectNew'
import TextFieldNew from 'src/components-new/inputs/TextFieldNew'
import UnitValueField, {UnitValue} from 'src/pages-new/therapies/dialog/UnitValueField'
import {
  THERAPY_DAILY_DOSAGE_UNIT_TYPE_OPTIONS,
  THERAPY_DOSAGE_UNIT_TYPE_OPTIONS,
  THERAPY_SPEED_OF_ASSUMPTION_UNIT_OPTIONS,
  THERAPY_SPEED_OF_DEVICE_UNIT_OPTIONS,
} from 'src/constants/constants'
import {useAppSelector} from 'src/hooks'
import apiClient from 'src/services/apiClient'
import {collectCustomFields, CustomFieldDef, CustomFieldValues, renderCustomField} from './customFields'

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

interface Option {
  value: string
  label: string
}

export interface DigitalEnrollmentFormValues {
  firstName: string
  lastName: string
  email?: string
  cellPhoneNumber?: string
  serviceType?: string
  therapyTypeId: string
  drugDosage?: UnitValue
  dailyDosage?: UnitValue
  speedOfAssumption?: UnitValue
  speedOfDevice?: UnitValue
  customFields: ReturnType<typeof collectCustomFields>
}

interface DigitalEnrollmentDialogNewProps {
  open: boolean
  onClose: () => void
  onSave: (values: DigitalEnrollmentFormValues) => void | Promise<void>
  therapyTypeOptions: Option[]
  serviceTypeOptions: Option[]
}

interface FormState {
  firstName: string
  lastName: string
  email: string
  cellPhoneNumber: string
  serviceType: string
  therapyTypeId: string
  drugDosage: UnitValue
  dailyDosage: UnitValue
  speedOfAssumption: UnitValue
  speedOfDevice: UnitValue
}

const INITIAL_STATE: FormState = {
  firstName: '',
  lastName: '',
  email: '',
  cellPhoneNumber: '',
  serviceType: '',
  therapyTypeId: '',
  // Each unit list currently has exactly one option — pre-select it so the "Unità"
  // dropdown isn't shown empty, matching the DosageStep.tsx convention of defaulting
  // to a known unit instead of leaving it for the user to open and pick.
  drugDosage: {unitType: THERAPY_DOSAGE_UNIT_TYPE_OPTIONS[0]?.value},
  dailyDosage: {unitType: THERAPY_DAILY_DOSAGE_UNIT_TYPE_OPTIONS[0]?.value},
  speedOfAssumption: {unitType: THERAPY_SPEED_OF_ASSUMPTION_UNIT_OPTIONS[0]?.value},
  speedOfDevice: {unitType: THERAPY_SPEED_OF_DEVICE_UNIT_OPTIONS[0]?.value},
}

const GRID_SX = {
  display: 'grid',
  gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)'},
  gap: 2,
} as const

/**
 * Creation-only dialog — new-style equivalent of
 * src/components/templates/NewEnrollmentDialog/NewEnrollmentDialog.tsx. Fields are
 * grouped into sections (Paziente / Terapia e dosaggi / Informazioni aggiuntive) instead
 * of the legacy 2-column CSS grid, but the submitted body shape is unchanged.
 *
 * Note: the legacy form revalidates email format/availability on every keystroke; this
 * validates once on save instead, to avoid firing `checkEmail` on every keypress — the
 * only intentional behavioral difference from the legacy dialog.
 */
export default function DigitalEnrollmentDialogNew({
  open,
  onClose,
  onSave,
  therapyTypeOptions,
  serviceTypeOptions,
}: DigitalEnrollmentDialogNewProps): JSX.Element {
  const {
    serviceTypeRequired,
    showServiceType,
    customFields = [],
    showDosage,
    showDailyDosage,
    showDeviceSpeed,
    showAdministrationSpeed,
  } = useAppSelector(state => state.pspConfigReducer.data.enrollmentHubConfigurations?.digitalEnrollmentConfigurations ?? {}) as {
    serviceTypeRequired?: boolean
    showServiceType?: boolean
    customFields?: CustomFieldDef[]
    showDosage?: boolean
    showDailyDosage?: boolean
    showDeviceSpeed?: boolean
    showAdministrationSpeed?: boolean
  }

  const [form, setForm] = useState<FormState>(INITIAL_STATE)
  const [customFieldValues, setCustomFieldValues] = useState<CustomFieldValues>({})
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm(prev => ({...prev, [key]: value}))
    setErrors(prev => ({...prev, [key]: ''}))
  }

  const setCustomFieldValue = (id: string, value: any) => {
    setCustomFieldValues(prev => ({...prev, [id]: value}))
    setErrors(prev => ({...prev, [`customField.${id}`]: ''}))
  }

  const handleClose = () => {
    if (saving) return
    setForm(INITIAL_STATE)
    setCustomFieldValues({})
    setErrors({})
    onClose()
  }

  const validate = async (): Promise<boolean> => {
    const nextErrors: Record<string, string> = {}

    if (!form.firstName.trim()) nextErrors.firstName = 'Campo obbligatorio'
    if (!form.lastName.trim()) nextErrors.lastName = 'Campo obbligatorio'

    if (!form.email && !form.cellPhoneNumber) {
      const message = 'Un input tra Email e Numero cellulare deve essere compilato'
      nextErrors.email = message
      nextErrors.cellPhoneNumber = message
    } else if (form.email && !EMAIL_REGEX.test(form.email)) {
      nextErrors.email = 'Formato email non valido'
    }

    if (showServiceType && serviceTypeRequired && !form.serviceType) nextErrors.serviceType = 'Campo obbligatorio'
    if (!form.therapyTypeId) nextErrors.therapyTypeId = 'Campo obbligatorio'

    customFields.forEach((field) => {
      if (field.required && (customFieldValues[field.id] === undefined || customFieldValues[field.id] === '')) {
        nextErrors[`customField.${field.id}`] = 'Campo obbligatorio'
      }
    })

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors)
      return false
    }

    if (form.email) {
      const isEmailAvailable = await apiClient.checkEmail(form.email, () => undefined)
      if (!isEmailAvailable) {
        setErrors({email: 'Email non disponibile'})
        return false
      }
    }

    return true
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const isValid = await validate()
      if (!isValid) return

      await onSave({
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email || undefined,
        cellPhoneNumber: form.cellPhoneNumber || undefined,
        ...(showServiceType && {serviceType: form.serviceType || undefined}),
        therapyTypeId: form.therapyTypeId,
        drugDosage: form.drugDosage.value !== undefined
          ? {value: form.drugDosage.value, unitType: form.drugDosage.unitType ?? THERAPY_DOSAGE_UNIT_TYPE_OPTIONS[0].value}
          : undefined,
        dailyDosage: form.dailyDosage.value !== undefined
          ? {value: form.dailyDosage.value, unitType: form.dailyDosage.unitType ?? THERAPY_DAILY_DOSAGE_UNIT_TYPE_OPTIONS[0].value}
          : undefined,
        speedOfAssumption: form.speedOfAssumption.value !== undefined
          ? {value: form.speedOfAssumption.value, unitType: form.speedOfAssumption.unitType ?? THERAPY_SPEED_OF_ASSUMPTION_UNIT_OPTIONS[0].value}
          : undefined,
        speedOfDevice: form.speedOfDevice.value !== undefined
          ? {value: form.speedOfDevice.value, unitType: form.speedOfDevice.unitType ?? THERAPY_SPEED_OF_DEVICE_UNIT_OPTIONS[0].value}
          : undefined,
        customFields: collectCustomFields(customFields, customFieldValues),
      })
      setForm(INITIAL_STATE)
      setCustomFieldValues({})
      setErrors({})
    } finally {
      setSaving(false)
    }
  }

  return (
    <AppDialog
      open={open}
      onClose={handleClose}
      title="Attivazione paziente"
      maxWidth="md"
      actions={(
        <>
          <Button variant="secondary" onClick={handleClose} disabled={saving}>{'Annulla'}</Button>
          <Button variant="primary" onClick={handleSave} loading={saving}>{'Salva'}</Button>
        </>
      )}
    >
      <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, pt: 1}}>
        <Card title="Paziente">
          <Box sx={GRID_SX}>
            <TextFieldNew
              label="Nome Paziente"
              value={form.firstName}
              onChange={v => setField('firstName', v)}
              required
              error={Boolean(errors.firstName)}
              helperText={errors.firstName}
              fullWidth
            />
            <TextFieldNew
              label="Cognome Paziente"
              value={form.lastName}
              onChange={v => setField('lastName', v)}
              required
              error={Boolean(errors.lastName)}
              helperText={errors.lastName}
              fullWidth
            />
            <TextFieldNew
              label="Email"
              type="email"
              value={form.email}
              onChange={v => setField('email', v)}
              error={Boolean(errors.email)}
              helperText={errors.email}
              fullWidth
            />
            <TextFieldNew
              label="Numero cellulare"
              type="tel"
              value={form.cellPhoneNumber}
              onChange={v => setField('cellPhoneNumber', v)}
              error={Boolean(errors.cellPhoneNumber)}
              helperText={errors.cellPhoneNumber}
              fullWidth
            />
          </Box>
        </Card>

        <Card title="Terapia e dosaggi">
          <Box sx={GRID_SX}>
            {showServiceType && (
              <SelectNew
                label="Tipologia servizio"
                value={form.serviceType}
                onChange={v => setField('serviceType', v)}
                options={serviceTypeOptions}
                required={serviceTypeRequired}
                error={Boolean(errors.serviceType)}
                helperText={errors.serviceType}
                fullWidth
              />
            )}
            <SelectNew
              label="Terapia"
              value={form.therapyTypeId}
              onChange={v => setField('therapyTypeId', v)}
              options={therapyTypeOptions}
              required
              error={Boolean(errors.therapyTypeId)}
              helperText={errors.therapyTypeId}
              fullWidth
            />
            {showDosage && (
              <UnitValueField
                label="Dosaggio"
                value={form.drugDosage}
                onChange={v => setField('drugDosage', v)}
                unitOptions={THERAPY_DOSAGE_UNIT_TYPE_OPTIONS}
              />
            )}
            {showDailyDosage && (
              <UnitValueField
                label="Dosaggio giornaliero"
                value={form.dailyDosage}
                onChange={v => setField('dailyDosage', v)}
                unitOptions={THERAPY_DAILY_DOSAGE_UNIT_TYPE_OPTIONS}
              />
            )}
            {showAdministrationSpeed && (
              <UnitValueField
                label="Velocità di somministrazione"
                value={form.speedOfAssumption}
                onChange={v => setField('speedOfAssumption', v)}
                unitOptions={THERAPY_SPEED_OF_ASSUMPTION_UNIT_OPTIONS}
              />
            )}
            {showDeviceSpeed && (
              <UnitValueField
                label="Velocità device"
                value={form.speedOfDevice}
                onChange={v => setField('speedOfDevice', v)}
                unitOptions={THERAPY_SPEED_OF_DEVICE_UNIT_OPTIONS}
              />
            )}
          </Box>
        </Card>

        {customFields.length > 0 && (
          <Card title="Informazioni aggiuntive">
            <Box sx={GRID_SX}>
              {customFields.map((field) =>
                renderCustomField(field, customFieldValues[field.id], (v) => setCustomFieldValue(field.id, v)))}
            </Box>
          </Card>
        )}
      </Box>
    </AppDialog>
  )
}
